/**
 * SwiperSlider Web Component
 *
 * - This Web Component provides a customizable Swiper slider that can be used in
 * any HTML document.
 * - It's designed to be mobile-responsive and can be configured using attributes.
 *
 * Usage:
 *
 * ```html
 * <swiper-slider
 *   js-options='{
 *     "speed": 400,
 *     "spaceBetween": 100,
 *     "loop": true
 *   }'
 *   js-mobile-only="true"
 *   class="swiper"
 * >
 *   <div class="swiper-wrapper">
 *     <div class="swiper-slide">Slide 1</div>
 *     <div class="swiper-slide">Slide 2</div>
 *     <div class="swiper-slide">Slide 3</div>
 *     <!-- Add more slides as needed -->
 *   </div>
 *
 *    <!-- Pagination -->
 *   <div class="swiper-pagination"></div>
 *
 *   <!-- Navigation -->
 *   <div class="swiper-button-prev"></div>
 *   <div class="swiper-button-next"></div>
 * </swiper-slider>
 * ```
 *
 * Attributes:
 *
 * - `js-options`: A JSON string of options for configuring the Swiper instance.
 *   - Example: `js-options='{"speed": 400, "spaceBetween": 100, "loop": true}'`
 *   - See Swiper documentation for available options: https://swiperjs.com/swiper-api
 *
 * - `js-mobile-only`: A boolean attribute that determines whether the slider
 * should only be active on mobile devices.
 *   - Example: `js-mobile-only="true"`
 *
 * Notes:
 *
 * - Ensure that Swiper's CSS and JavaScript are loaded in your project.
 * You can include them via a CDN:
 *
 * ```html
 *  <link
 *    rel="stylesheet"
 *    href="https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css"
 *  />
 *  <script src="https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js" defer></script>
 * ```
 * - See Swiper documentation for installation: https://swiperjs.com/get-started
 *
 * - This component automatically destroys and reinitializes the Swiper instance
 * based on screen size if `js-mobile-only` is set.
 */

if ('customElements' in window && !customElements.get('swiper-slider')) {
  /**
   * Custom element for a Swiper slider.
   * @extends HTMLElement
   */
  class SwiperSlider extends HTMLElement {
    constructor() {
      super();

      /**
       * A flag to determine if the slider should only be active on mobile devices.
       * @type {boolean}
       */
      this.mobileOnly = this.parseAttributeValue(
        this.getAttribute('js-mobile-only')
      );
    }

    /**
     * Invoked when the element is added to the DOM.
     */
    connectedCallback() {
      this.handleSliderInitialization();

      // Add a resize event listener with debouncing
      window.addEventListener(
        'resize',
        this.debounce(this.handleSliderInitialization.bind(this), 100)
      );
    }

    /**
     * Invoked when the element is removed from the DOM.
     * Ensures the swiper instance is destroyed.
     */
    disconnectedCallback() {
      this.destroySlider();
    }

    /**
     * Retrieves and parses the slider options from the element's attributes.
     * @returns {Object} The parsed options object for Swiper initialization.
     */
    getOptions() {
      const jsOptions = this.parseAttributeValue(
        this.getAttribute('js-options')
      );

      return jsOptions;
    }

    /**
     * Initializes the Swiper slider with the provided options.
     */
    initSlider() {
      // Parse options from the js-options attribute
      const options = this.getOptions();
      const swiperTarget = this;

      // Initialize Swiper
      this.swiper = new Swiper(swiperTarget, options);
    }

    /**
     * Destroys the Swiper slider instance.
     * Ensures that all event listeners are removed and the instance is cleaned up.
     */
    destroySlider() {
      this.swiper?.destroy();
    }

    /**
     * Handles the initialization of the slider based on the current screen size.
     * Initializes or destroys the slider based on the mobile-only setting.
     */
    handleSliderInitialization() {
      // Check if the slider should be initialized on the current screen size
      if (!this.mobileOnly || window.matchMedia(`(max-width: 768px)`).matches) {
        this.initSlider();
        return;
      } else {
        this.destroySlider();
      }
    }

    /**
     * Parse and format the attribute value based on its type.
     * @param {string} value - The attribute value to be parsed.
     * @returns {*} - The parsed and formatted attribute value.
     */
    parseAttributeValue(value) {
      try {
        // Attempt to parse the value as JSON
        const parsedValue = JSON.parse(value);

        // If not a boolean or JSON, handle it as a string
        return typeof parsedValue === 'object' ||
          typeof parsedValue === 'boolean'
          ? parsedValue
          : value;
      } catch (error) {
        // If JSON parse fails, handle it as a string
        return value;
      }
    }

    /**
     * Debounce function to rate-limit execution of a callback function.
     * @param {Function} callback - The function to debounce.
     * @param {number} wait - The amount of time in milliseconds before the callback is invoked.
     * @param {boolean} [immediate=false] - If true, the callback is triggered on the leading edge, instead of the trailing.
     * @returns {Function} - A debounced version of the original callback function.
     */
    debounce(callback, wait, immediate = false) {
      let timeout = null;

      return function (...args) {
        const callNow = immediate && !timeout;

        /**
         * Function to be called after the wait time has elapsed.
         */
        function later() {
          timeout = null;
          if (!immediate) {
            callback.apply(this, args);
          }
        }

        window.clearTimeout(timeout);
        timeout = window.setTimeout(later, wait);

        if (callNow) {
          callback.apply(this, args);
        }
      };
    }
  }

  // Define the custom element
  customElements.define('swiper-slider', SwiperSlider);
}
